import {
  CurrenciesListData,
  currenciesListInfiniteOptions,
  CurrencyReadable,
  opportunitiesProductsCreateMutation,
  opportunitiesProductsListQueryKey,
  opportunitiesProductsPartialUpdateMutation,
  opportunitiesProductsRetrieveOptions,
  opportunitiesProductsRetrieveQueryKey,
  opportunitiesRetrieveQueryKey,
  PaginatedCurrencyListReadable,
  PaginatedProductUoMListReadable,
  PaginatedReturnOpportunityProductListReadable,
  PaginatedReturnProductListReadable,
  ProductsListData,
  productsListInfiniteOptions,
  ProductsUnitsOfMeasureListData,
  productsUnitsOfMeasureListInfiniteOptions,
  ProductUoMReadable,
  ReturnOpportunityProductReadable,
  ReturnOpportunityReadable,
  ReturnProductReadable
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import {
  composePrice,
  getLineTotal,
  getUsedUomsProduct,
  setProductDefaults
} from '@crm/utils'
import { Text } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { showApiErrors, transformOptions } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, Col, Form, InputNumber, message, Row } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

type Props = {
  id?: string | number
  formName?: string
  onCancel: () => void
  opportunityData?: ReturnOpportunityReadable
  opportunityProducts?: PaginatedReturnOpportunityProductListReadable
  onLoadingChange: (loading: boolean) => void
}

export const ProductForm = ({
  id,
  formName,
  onCancel,
  opportunityData,
  opportunityProducts,
  onLoadingChange
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { opportunityId } = useParams()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const [selectedCurrency, setSelectedCurrency] = useState<string>()
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null
  )
  const opportunityCurrency = opportunityData?.currency
  const isFirstProduct =
    !opportunityProducts || opportunityProducts.results.length === 0

  const { data: opportunityProduct } = useQuery({
    ...opportunitiesProductsRetrieveOptions({
      path: { id: Number(id) }
    }),
    enabled: !!id
  })

  const onMutate = async () => {
    await invalidateQueries([
      opportunitiesProductsListQueryKey(),
      opportunitiesProductsRetrieveQueryKey({ path: { id: Number(id) } })
    ])
    invalidateQueries([
      opportunitiesRetrieveQueryKey({ path: { id: Number(opportunityId) } })
    ])
    onCancel()
    message.success(t('success.success_data_save'))
  }

  const { mutate: createMutate, isPending: isCreating } = useMutation({
    ...opportunitiesProductsCreateMutation(),
    onSuccess: () => onMutate(),
    onError: (e) => showApiErrors(e, form)
  })

  const { mutate: editMutate, isPending: isEditing } = useMutation({
    ...opportunitiesProductsPartialUpdateMutation(),
    onSuccess: () => onMutate(),
    onError: (e) => showApiErrors(e, form)
  })

  const isLoading = isCreating || isEditing

  useEffect(() => {
    if (opportunityProduct) {
      const productId = opportunityProduct.product?.id
      const usedNow = getUsedUomsProduct(
        opportunityProducts,
        productId,
        opportunityProduct.id
      )

      form.setFieldsValue({
        product: id ? opportunityProduct?.product?.name : productId,
        uom: usedNow.includes(opportunityProduct.uom?.id)
          ? undefined
          : opportunityProduct.uom?.id,
        currency: id ? opportunityCurrency?.name : opportunityCurrency?.id,
        extra_discount: opportunityProduct.extra_discount,
        custom_price: opportunityProduct.custom_price,
        quantity: opportunityProduct.quantity
      })

      setSelectedProductId(productId)
    } else {
      form.setFieldsValue({
        currency: !isFirstProduct
          ? opportunityCurrency?.name
          : opportunityCurrency?.id
      })
    }
  }, [
    opportunityProduct,
    opportunityProducts,
    opportunityCurrency,
    id,
    isFirstProduct,
    form
  ])

  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [onLoadingChange, isLoading])

  const onSubmit = async () => {
    const values = await form.validateFields()

    if (id) {
      if (!opportunityProduct?.product?.id) {
        message.error(t('error.product_not_found'))
        return
      }

      editMutate({
        path: { id: Number(id) },
        body: {
          ...values,
          product: id ? opportunityProduct?.product?.id : values.product,
          currency: id ? opportunityCurrency?.id : values.currency,
          opportunity: opportunityId
        }
      })
    } else {
      createMutate({
        body: {
          ...values,
          currency: !isFirstProduct ? opportunityCurrency?.id : values.currency,
          opportunity: opportunityId
        }
      })
    }
  }

  return (
    <Form form={form} name={formName} onFinish={onSubmit} layout="vertical">
      <Alert
        className="mb-20"
        message={t('opportunity.opportunity_currency_from_first_product')}
        type="warning"
      />

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item
            name="product"
            label={t('task.product')}
            rules={[{ required: true }]}
          >
            <InfiniteSelect<
              ProductsListData,
              PaginatedReturnProductListReadable,
              ReturnProductReadable
            >
              popupMatchSelectWidth
              placeholder={t('main.select')}
              getQueryFunction={productsListInfiniteOptions}
              getOptions={({ options }) => {
                return transformOptions({
                  options: options,
                  label: 'name',
                  value: 'id',
                  returnOption: true
                })
              }}
              onSelect={(_, option) => {
                const productOption = option as ReturnProductReadable
                form.setFieldValue('product', productOption.id)
                setProductDefaults(
                  form,
                  productOption,
                  opportunityProducts,
                  id ? Number(id) : undefined,
                  isFirstProduct
                )
                setSelectedProductId(productOption.id)
                setSelectedCurrency(productOption?.currency?.symbol)
              }}
              disabled={!!id}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={8}>
          <Form.Item label={t(`opportunity.price`)} name="custom_price">
            <InputNumber min={0} placeholder="100" />
          </Form.Item>
        </Col>

        <Col xs={24} md={8}>
          <Form.Item name="currency" label={t('contracts.currency')}>
            <InfiniteSelect<
              CurrenciesListData,
              PaginatedCurrencyListReadable,
              CurrencyReadable
            >
              popupMatchSelectWidth
              placeholder={t('main.select')}
              getQueryFunction={currenciesListInfiniteOptions}
              getOptions={({ options }) => {
                return transformOptions({
                  options: options,
                  label: 'name',
                  value: 'id',
                  returnOption: true
                })
              }}
              onSelect={(_, option) => {
                const currency = option as CurrencyReadable
                setSelectedCurrency(currency?.symbol)
              }}
              disabled={!isFirstProduct}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={8}>
          <Form.Item name="uom" label={t('opportunity.unit_of_measure')}>
            <InfiniteSelect<
              ProductsUnitsOfMeasureListData,
              PaginatedProductUoMListReadable,
              ProductUoMReadable
            >
              popupMatchSelectWidth
              placeholder={t('main.select')}
              getQueryFunction={productsUnitsOfMeasureListInfiniteOptions}
              getOptions={({ options }) => {
                const usedNow = getUsedUomsProduct(
                  opportunityProducts,
                  selectedProductId,
                  id ? Number(id) : undefined
                )

                return options
                  .filter((option) => !usedNow.includes(option.id))
                  .map((option) => ({ label: option.name, value: option.id }))
              }}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={8}>
          <Form.Item label={t(`billing.quantity`)} name="quantity">
            <InputNumber min={1} placeholder="1" />
          </Form.Item>
        </Col>

        <Col xs={24} md={8}>
          <Form.Item label={t(`opportunity.discount`)} name="extra_discount">
            <InputNumber
              min={0}
              max={100}
              placeholder="10"
              suffix="%"
              className="w-full"
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item shouldUpdate>
        {() => {
          const price = form.getFieldValue('custom_price')
          const quantity = form.getFieldValue('quantity')
          const discount = form.getFieldValue('extra_discount')

          const tempProduct = {
            custom_price: price ?? 0,
            quantity: quantity ?? 1,
            extra_discount: discount ?? 0
          } as ReturnOpportunityProductReadable

          const lineTotal = getLineTotal(tempProduct)

          return (
            <Text weight={500}>
              {t('opportunity.total', {
                total: composePrice(
                  lineTotal,
                  opportunityCurrency
                    ? opportunityCurrency?.symbol
                    : selectedCurrency
                )
              })}
            </Text>
          )
        }}
      </Form.Item>
    </Form>
  )
}
